import type { AxiosInstance } from 'axios';
import { apiClient } from '../client/client';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type {
  SellerRatingViewModel,
  SellerRatingCreateRequest,
  SellerRatingAggregateViewModel,
  PaginatedRatingList,
} from '../types/rating';

export interface SellerRatingsQueryParams {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Post-purchase seller ratings. See contracts/rating.http.md.
 */
export class RatingService {
  private static readonly BASE = '/api/ratings';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async submit(
    request: SellerRatingCreateRequest
  ): Promise<ServiceResultT<SellerRatingViewModel>> {
    const { data } = await this.client.post<ServiceResultT<SellerRatingViewModel>>(
      RatingService.BASE,
      request
    );
    return data;
  }

  async getBySoldListing(
    soldListingId: string
  ): Promise<ServiceResultT<SellerRatingViewModel | null>> {
    const { data } = await this.client.get<ServiceResultT<SellerRatingViewModel | null>>(
      `${RatingService.BASE}/by-sold-listing/${soldListingId}`
    );
    return data;
  }

  async listForSeller(
    sellerId: string,
    params?: SellerRatingsQueryParams
  ): Promise<ServiceResultT<PaginatedRatingList>> {
    const { data } = await this.client.get<ServiceResultT<PaginatedRatingList>>(
      `${RatingService.BASE}/seller/${sellerId}`,
      { params }
    );
    return data;
  }

  async getAggregate(
    sellerId: string
  ): Promise<ServiceResultT<SellerRatingAggregateViewModel>> {
    const { data } = await this.client.get<
      ServiceResultT<SellerRatingAggregateViewModel>
    >(`${RatingService.BASE}/seller/${sellerId}/aggregate`);
    return data;
  }
}

export const ratingService = new RatingService();
